import { useFormikContext } from "formik";
import type { OrganizationMember } from "@common/dto/organization/organization-member.dto";
import {
    ENGAGEMENT_TEAM_ROLES,
    TaskTemplateAssigneeRole,
} from "@common/types/taskTemplate";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import type { ApplyCloseTemplateWizardValues } from "./applyCloseTemplate.schema";
import { reconcileWorkEndDate } from "./applyCloseTemplateWorkDates";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { ClosePeriodSelector } from "./ClosePeriodSelector";
import { MemberCombobox } from "./MemberCombobox";
import { useTemplateApplication } from "./useTemplateApplication";
import styles from "./ApplyCloseTemplateDrawer.module.scss";

const fields = createFormikFields<ApplyCloseTemplateWizardValues>();

interface Props {
    onBack: () => void;
    onContinue: () => void;
    /** Members who can access at least one org workspace (assignable defaults). */
    memberOptions: OrganizationMember[];
    /** Roles that have at least one task in the template (controls which fields are shown). */
    activeRoles: Set<TaskTemplateAssigneeRole>;
}

export const ApplyCloseTemplateStepDefaults = ({
    onBack,
    onContinue,
    memberOptions,
    activeRoles,
}: Props) => {
    const formik = useFormikContext<ApplyCloseTemplateWizardValues>();
    const { getMemberLabel } = useTemplateApplication();

    return (
        <div>
            <div className={styles.subtitleContainer}>
                Step 2 of 4 · Template details
            </div>
            <div className={styles.centeredContent}>
                <div className={styles.serviceDetailsHeader}>
                    Template details
                </div>
                <div className={styles.subTitle}>
                    Service period
                    <div className={styles.sectionDescription}>
                        Tasks in this service only show transactions and reports
                        from this period.
                    </div>
                </div>
                <ClosePeriodSelector
                    startDate={formik.values.defaults.startDate}
                    endDate={formik.values.defaults.endDate}
                    onStartDateChange={(value) =>
                        void formik.setFieldValue("defaults.startDate", value)
                    }
                    onEndDateChange={(value) =>
                        void formik.setFieldValue("defaults.endDate", value)
                    }
                />

                <div className={styles.subTitle}>Schedule</div>
                <div className={styles.periodSelectorRow}>
                    <fields.Date
                        className={styles.periodSelectItem}
                        name="defaults.workStartDate"
                        label="Work start"
                        variant="panel-input"
                        required
                        placeholder="Required"
                        buttonProps={{
                            variant: "primary",
                            size: "xl",
                            block: true,
                        }}
                        onValueChange={(workStartDate) =>
                            void formik.setFieldValue(
                                "defaults.workDeliveryDate",
                                reconcileWorkEndDate(
                                    workStartDate,
                                    formik.values.defaults.workDeliveryDate,
                                ),
                            )
                        }
                    />
                    <fields.Date
                        className={styles.periodSelectItem}
                        name="defaults.workDeliveryDate"
                        label="Work end"
                        variant="panel-input"
                        required
                        minDate={
                            formik.values.defaults.workStartDate ?? undefined
                        }
                        placeholder="Required"
                        buttonProps={{
                            variant: "primary",
                            size: "xl",
                            block: true,
                        }}
                    />
                </div>

                {ENGAGEMENT_TEAM_ROLES.some((role) =>
                    activeRoles.has(role),
                ) && <div className={styles.subTitle}>Team</div>}
                <VStack gap={16}>
                    {activeRoles.has(TaskTemplateAssigneeRole.PREPARER) && (
                        <MemberCombobox
                            label="Preparer"
                            value={formik.values.defaults.preparerId ?? null}
                            members={memberOptions}
                            getMemberLabel={getMemberLabel}
                            onValueChange={(v) =>
                                void formik.setFieldValue(
                                    "defaults.preparerId",
                                    v,
                                )
                            }
                        />
                    )}

                    {activeRoles.has(TaskTemplateAssigneeRole.REVIEWER) && (
                        <MemberCombobox
                            label="Reviewer"
                            value={formik.values.defaults.reviewerId ?? null}
                            members={memberOptions}
                            getMemberLabel={getMemberLabel}
                            onValueChange={(v) =>
                                void formik.setFieldValue(
                                    "defaults.reviewerId",
                                    v,
                                )
                            }
                        />
                    )}

                    {activeRoles.has(TaskTemplateAssigneeRole.MANAGER) && (
                        <MemberCombobox
                            label="Manager"
                            value={formik.values.defaults.managerId ?? null}
                            members={memberOptions}
                            getMemberLabel={getMemberLabel}
                            onValueChange={(v) =>
                                void formik.setFieldValue(
                                    "defaults.managerId",
                                    v,
                                )
                            }
                        />
                    )}
                </VStack>

                <HStack as="footer" justify="end" gap={12} className="mt-4">
                    <ButtonWithLoader
                        variant="secondary"
                        size="xl"
                        type="button"
                        loading={false}
                        onClick={onBack}
                    >
                        Back
                    </ButtonWithLoader>
                    <ButtonWithLoader
                        variant="primary"
                        size="xl"
                        type="button"
                        loading={false}
                        onClick={onContinue}
                        hotKey="mod+enter"
                        hotKeyOptions={{
                            description: "Select clients",
                            preventDefault: true,
                        }}
                        hotKeyDisplayPosition="inline"
                    >
                        Select clients
                    </ButtonWithLoader>
                </HStack>
            </div>
        </div>
    );
};
